import { Breadcrumb, Checkbox, Col, Container, Input, Item, List, Loading, Row } from "react-dsgov";
import CardApi from "./CardApi/CardApi";
import { useEffect, useMemo, useState } from "react";
import IApi from "../../interfaces/IApi";
import { useNavigate } from "react-router";
import useCatalogoService from "../../hooks/useCatalogoService";
import useLoadingState from "../../state/hooks/useLoadingState";
import useSubtituloState from "../../state/hooks/useSubtituloState";
import { useClear } from "../../hooks/useClear";
import classNames from "classnames";
import useMessageScroll from "../../hooks/useMessageScroll";
import { buildPageTitle } from "../../config/portalMode";
import PageMetadata from "../../components/PageMetadata/PageMetadata";

interface IFiltroOpcao {
    id: string;
    nome: string;
}

const CatalogoAPIs: React.FC = () => {
    const navigate = useNavigate();
    const catalogoService = useCatalogoService();
    const clear = useClear();

    const [, setSubtitulo] = useSubtituloState();
    const [, setLoading] = useLoadingState();

    const [apis, setApis] = useState<IApi[]>([]);
    const [carregando, setCarregando] = useState<boolean>(false);

    const [tecnologiasSelecionadas, setTecnologiasSelecionadas] = useState<string[]>([]);
    const [controlesAcessoSelecionados, setControlesAcessoSelecionados] = useState<string[]>([]);
    const [modelosOfertaSelecionados, setModelosOfertaSelecionados] = useState<string[]>([]);
    const [solucoesSelecionadas, setSolucoesSelecionadas] = useState<string[]>([]);
    const [tagsSelecionadas, setTagsSelecionadas] = useState<string[]>([]);
    const [textoBusca, setTextoBusca] = useState<string>("");

    const messageScroll = useMessageScroll();

    useEffect(() => {
        setSubtitulo("Catálogo de APIs");
    }, [setSubtitulo]);

    useEffect(() => {
        setCarregando(true);
        setLoading(true);

        catalogoService.getApisListagem()
            .then(apis => {
                setApis(apis);
            })
            .finally(() => {
                setCarregando(false);
                setLoading(false);
            });
    }, [catalogoService, setLoading]);

    const tecnologias = useMemo(() => {
        const valores = apis.map(a => a.tecnologia).filter((t): t is string => !!t);
        return Array.from(new Set(valores)).sort().map(nome => ({ id: nome, nome }));
    }, [apis]);

    const handleFilterChange = (
        id: string,
        checked: boolean,
        setSelecionados: React.Dispatch<React.SetStateAction<string[]>>
    ) => {
        messageScroll();

        setSelecionados(prevIds => {
            if (checked) {
                return [...prevIds, id];
            }
            return prevIds.filter(prevId => prevId !== id);
        });
    };

    const controlesAcesso = useMemo(() => {
        const valores = apis.map(a => a.controleAcesso).filter((c): c is string => !!c);
        return Array.from(new Set(valores)).sort().map(nome => ({ id: nome, nome }));
    }, [apis]);

    const modelosOferta = useMemo(() => {
        const valores = apis.map(a => a.modeloOferta).filter((m): m is string => !!m);
        return Array.from(new Set(valores)).sort().map(nome => ({ id: nome, nome }));
    }, [apis]);

    const solucoes = useMemo(() => {
        const valores = apis.map(a => a.siglaSolucao).filter((s): s is string => !!s);
        return Array.from(new Set(valores)).sort().map(nome => ({ id: nome, nome }));
    }, [apis]);

    const tags = useMemo(() => {
        const valores = apis
            .flatMap(a => (a.tags || "").split(/[\s,;|]+/).filter(t => t))
            .filter((t): t is string => !!t);
        return Array.from(new Set(valores)).sort().map(nome => ({ id: nome, nome }));
    }, [apis]);

    const apisFiltradas = useMemo(() => {
        const textoNormalizado = textoBusca.trim().toLocaleLowerCase();

        return apis.filter(api => {
            if (textoNormalizado && !JSON.stringify(api).toLocaleLowerCase().includes(textoNormalizado)) {
                return false;
            }
            if (tecnologiasSelecionadas.length > 0 && !tecnologiasSelecionadas.includes(api.tecnologia || "")) {
                return false;
            }
            if (controlesAcessoSelecionados.length > 0 && !controlesAcessoSelecionados.includes(api.controleAcesso || "")) {
                return false;
            }
            if (modelosOfertaSelecionados.length > 0 && !modelosOfertaSelecionados.includes(api.modeloOferta || "")) {
                return false;
            }
            if (solucoesSelecionadas.length > 0 && !solucoesSelecionadas.includes(api.siglaSolucao || "")) {
                return false;
            }
            if (tagsSelecionadas.length > 0) {
                const apiTags = (api.tags || "").split(/[\s,;|]+/).filter(t => t);
                if (!tagsSelecionadas.some(tag => apiTags.includes(tag))) {
                    return false;
                }
            }
            return true;
        });
    }, [apis, textoBusca, tecnologiasSelecionadas, controlesAcessoSelecionados, modelosOfertaSelecionados, solucoesSelecionadas, tagsSelecionadas]);

    const renderFiltro = (
        titulo: string,
        opcoes: IFiltroOpcao[],
        selecionados: string[],
        setSelecionados: React.Dispatch<React.SetStateAction<string[]>>
    ) => {
        if (opcoes.length <= 1) return null;

        return (
            <Item showDividerAfter collapsable initiallyExpanded subItems={
                <>
                    {opcoes.map((opcao) => (
                        <Item key={opcao.id}>
                            <Checkbox
                                id={`checkbox-${titulo}-${opcao.id}`}
                                label={opcao.nome}
                                checked={selecionados.includes(opcao.id)}
                                onChange={(event) => handleFilterChange(opcao.id, (event.target as HTMLInputElement).checked, setSelecionados)}
                            />
                        </Item>
                    ))}
                </>
            }>
                {titulo}
            </Item>
        );
    };

    return (
        <>
            <PageMetadata title={buildPageTitle("Catálogo de APIs")} />
            <Container fluid={clear} className={classNames({ "sem-margin": clear })}>
                {!clear && (
                    <Breadcrumb>
                        <Breadcrumb.Item home href="#" onClick={() => navigate("/")}></Breadcrumb.Item>
                        <Breadcrumb.Item>Catálogo de APIs</Breadcrumb.Item>
                    </Breadcrumb>
                )}
                <Row>
                    <Col md={3}>
                        <List title="Filtros">
                            <Item showDividerAfter>
                                <Input
                                    id="busca-apis"
                                    type="search"
                                    label="Buscar nas APIs"
                                    value={textoBusca}
                                    onChange={(event) => setTextoBusca((event.target as HTMLInputElement).value)}
                                    placeholder="Digite um termo"
                                />
                            </Item>
                            {renderFiltro("Sistema", solucoes, solucoesSelecionadas, setSolucoesSelecionadas)}
                            {renderFiltro("Tecnologias", tecnologias, tecnologiasSelecionadas, setTecnologiasSelecionadas)}
                            {renderFiltro("Controle de acesso", controlesAcesso, controlesAcessoSelecionados, setControlesAcessoSelecionados)}
                            {renderFiltro("Modelo de oferta", modelosOferta, modelosOfertaSelecionados, setModelosOfertaSelecionados)}
                        </List>
                    </Col>
                    <Col>
                        {carregando && <Loading />}

                        {!carregando && apisFiltradas.length === 0 && (
                            <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                                <p className="h5 text-muted">Sem resultados para os filtros selecionados.</p>
                            </div>
                        )}

                        {!carregando && apisFiltradas.length > 0 && (
                            <div className="mb-5 d-flex mt-1 align-content-start justify-content-center flex-wrap">
                                {apisFiltradas.map((api) => (
                                    <CardApi key={api.idRotina} api={api} />
                                ))}
                            </div>
                        )}
                    </Col>
                </Row>
            </Container>
        </>
    );
}

export default CatalogoAPIs;
